import { formatNumbersInString, thousandSeparator } from "./dataFormatter";

import {
  parse,
  parseISO,
  isValid as isValidDate,
  format as dfFormat,
} from "date-fns";

const fieldNamesWithoutFormatting = ["year", "EventYear", "InfoObjectID"];
const kmPositionNumberKeys = new Set([
  "Position",
  "Ending",
  "startPosition",
  "endPosition",
]);
const kmPlusStringKeys = new Set([
  "MaintenancePosition",
  "MaintenanceEnding",
]);
const numberLikeRegexp = /\d+\.\d{3,}/;

/** Строгое парсинг по формату */
function parseStrict(value: string, fmt: string): Date | null {
  const d = parse(value, fmt, new Date());
  return isValidDate(d) && dfFormat(d, fmt) === value ? d : null;
}

/** Попробовать распарсить как ISO 8601 (учитывает Z/+03:00 и т.п.) */
function tryParseISO(value: string): Date | null {
  try {
    const d = parseISO(value);
    return isValidDate(d) ? d : null;
  } catch {
    return null;
  }
}

export function formatObject(input: any): any {
  if (input === null || typeof input !== "object") return input;

  if (Array.isArray(input)) {
    return formatArray(input);
  }

  const result: Record<string, any> = {};

  for (const [key, value] of Object.entries(input)) {
    result[key] = formatValue(key, value);
  }

  return result;
}

function formatValue(key: string, value: any): any {
  if (
    fieldNamesWithoutFormatting.includes(key) ||
    value === null ||
    typeof value === "boolean"
  ) {
    return value;
  }

  if (typeof value === "string") {
    return formatString(key, value);
  }

  if (typeof value === "number") {
    return formatNumber(key, value);
  }

  if (Array.isArray(value)) {
    return formatArray(value);
  }

  if (typeof value === "object") {
    return formatObject(value);
  }

  return value;
}

function formatString(key: string, str: string): string | null {
  if (str.trim() === "") return null;

  // Специальное форматирование для строк "км+м" вида "12;345" → "12 + 345"
  if (kmPlusStringKeys.has(key)) {
    const parts = str.split(";");
    if (parts.length >= 2) {
      const km = parts[0].trim();
      const m = parts[1].trim().padStart(3, "0");
      return `${km} + ${m}`;
    }
  }

  // Сначала проверим "чистую" дату yyyy-MM-dd → dd.MM.yyyy
  const onlyDate = parseStrict(str, "yyyy-MM-dd");
  if (onlyDate) {
    return dfFormat(onlyDate, "dd.MM.yyyy");
  }

  // Затем пробуем форматы с временем → dd.MM.yyyy HH:mm
  const ymdHm = parseStrict(str, "yyyy-MM-dd'T'HH:mm");
  if (ymdHm) {
    return dfFormat(ymdHm, "dd.MM.yyyy HH:mm");
  }

  const ymdHms = parseStrict(str, "yyyy-MM-dd'T'HH:mm:ss");
  if (ymdHms) {
    return dfFormat(ymdHms, "dd.MM.yyyy HH:mm");
  }

  // ISO-8601: пробуем только если в строке есть '-' или 'T' (иначе "3920" ошибочно парсится как год)
  const iso = /[-T]/.test(str) ? tryParseISO(str) : null;
  if (iso) {
    return dfFormat(iso, "dd.MM.yyyy HH:mm");
  }

  // Числа в строке с «длинной» дробной частью
  if (numberLikeRegexp.test(str)) {
    return formatNumbersInString(str);
  }

  if (/^\d{1,4}$/.test(str)) {
    return str;
  }

  return str;
}

function formatNumber(key: string, num: number): string {
  // Специальное форматирование для полей Начало/Конец, км: всегда 3 знака после запятой
  if (kmPositionNumberKeys.has(key)) {
    return thousandSeparator(num.toFixed(3));
  }

  return Number.isInteger(num)
    ? thousandSeparator(num.toString())
    : thousandSeparator(num.toFixed(3));
}

function formatArray(arr: any[]): any[] {
  return arr.map((item) => {
    if (typeof item === "number") {
      return Number(item.toFixed(6));
    }

    if (Array.isArray(item)) {
      return item.map((subItem) =>
        typeof subItem === "number" ? Number(subItem.toFixed(6)) : formatObject(subItem)
      );
    }

    if (typeof item === "object") {
      return formatObject(item);
    }

    return item;
  });
}
